import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Building, Calendar, Clock, FileText, Loader2, CheckCircle, AlertCircle, MapPin, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from './ui/Button';
import { supabase } from '../lib/auth';
import { getClients } from '../lib/api';

interface AddAppointmentFormProps {
  onSuccess: (appointment: any) => void;
  onCancel: () => void;
}

interface Client {
  id: string;
  name: string;
  company_name: string;
}

const AddAppointmentForm: React.FC<AddAppointmentFormProps> = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    prospect_name: '',
    prospect_linkedin: '',
    job_title: '',
    company_name: '',
    company_linkedin: '',
    phone: '',
    description: '',
    location: '',
    notes: '',
    client_id: '',
    email_sales_hook: '',
    status: 'planifié'
  });
  
  const [clients, setClients] = useState<Client[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    const fetchClientsData = async () => {
      try {
        const data = await getClients();
        setClients(data || []);
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };

    fetchClientsData();
  }, []);

  // Real-time validation
  const validateField = (field: string, value: string) => {
    const errors = { ...fieldErrors };
    
    switch (field) {
      case 'title':
        if (!value.trim()) {
          errors.title = 'Le titre est requis';
        } else if (value.trim().length < 3) {
          errors.title = 'Le titre doit contenir au moins 3 caractères';
        } else {
          delete errors.title;
        }
        break;
      case 'date':
        if (!value) {
          errors.date = 'La date est requise';
        } else {
          delete errors.date;
        }
        break;
      case 'time':
        if (!value) {
          errors.time = 'L\'heure est requise';
        } else {
          delete errors.time;
        }
        break;
      case 'prospect_name':
        if (!value.trim()) {
          errors.prospect_name = 'Le nom du prospect est requis';
        } else if (value.trim().length < 2) {
          errors.prospect_name = 'Le nom doit contenir au moins 2 caractères';
        } else {
          delete errors.prospect_name;
        }
        break;
      case 'company_name':
        if (!value.trim()) {
          errors.company_name = 'Le nom de l\'entreprise est requis';
        } else if (value.trim().length < 2) {
          errors.company_name = 'Le nom de l\'entreprise doit contenir au moins 2 caractères';
        } else {
          delete errors.company_name;
        }
        break;
      case 'client_id':
        if (!value) {
          errors.client_id = 'Veuillez sélectionner un client';
        } else {
          delete errors.client_id;
        }
        break;
      case 'prospect_linkedin':
        if (value && !value.match(/^https?:\/\/(www\.)?linkedin\.com\//)) {
          errors.prospect_linkedin = 'Veuillez entrer une URL LinkedIn valide';
        } else {
          delete errors.prospect_linkedin;
        }
        break;
      case 'company_linkedin':
        if (value && !value.match(/^https?:\/\/(www\.)?linkedin\.com\//)) {
          errors.company_linkedin = 'Veuillez entrer une URL LinkedIn valide';
        } else {
          delete errors.company_linkedin;
        }
        break;
      case 'phone':
        if (value && !/^[\+]?[0-9\s\-\(\)]{8,}$/.test(value.trim())) {
          errors.phone = 'Veuillez entrer un numéro de téléphone valide';
        } else {
          delete errors.phone;
        }
        break;
      case 'email_sales_hook':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          errors.email_sales_hook = 'Veuillez entrer une adresse email valide';
        } else {
          delete errors.email_sales_hook;
        }
        break;
    }

    setFieldErrors(errors);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all required fields
    const requiredFields = ['title', 'date', 'time', 'prospect_name', 'company_name', 'client_id'];
    requiredFields.forEach(field => {
      validateField(field, formData[field as keyof typeof formData]);
    });
    
    if (Object.keys(fieldErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create the appointment
      const { data, error } = await supabase
        .from('appointments')
        .insert([{
          title: formData.title.trim(),
          date: formData.date,
          time: formData.time,
          prospect_name: formData.prospect_name.trim(),
          prospect_linkedin: formData.prospect_linkedin.trim() || null,
          job_title: formData.job_title.trim() || null,
          company_name: formData.company_name.trim(),
          company_linkedin: formData.company_linkedin.trim() || null,
          phone: formData.phone.trim() || null,
          description: formData.description.trim() || null,
          location: formData.location.trim() || null,
          notes: formData.notes.trim() || null,
          client_id: formData.client_id,
          email_sales_hook: formData.email_sales_hook.trim() || null,
          status: formData.status,
          booking_date: new Date().toISOString()
        }])
        .select(`
          *,
          clients (
            id,
            name
          )
        `)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Failed to create appointment');

      // Call success callback
      onSuccess(data);
      
    } catch (err: any) {
      console.error('Error creating appointment:', err);
      if (err.message?.includes('duplicate key value')) {
        setError('Un rendez-vous similaire existe déjà');
      } else {
        setError(err.message || 'Erreur lors de la création du rendez-vous');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.title && formData.date && formData.time && formData.prospect_name && formData.company_name && formData.client_id && Object.keys(fieldErrors).length === 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center"
        >
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </motion.div>
      )}

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
            <FileText className="h-4 w-4 inline mr-1" />
            Titre du rendez-vous *
          </label>
          <input
            id="title"
            name="title"
            type="text"
            required
            value={formData.title}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              fieldErrors.title 
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
            }`}
            placeholder="Ex: Démonstration produit, Réunion stratégique..."
          />
          {fieldErrors.title && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-600 flex items-center"
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              {fieldErrors.title}
            </motion.p>
          )}
        </div>

        {/* Client */}
        <div>
          <label htmlFor="client_id" className="block text-sm font-medium text-gray-700 mb-2">
            <Building className="h-4 w-4 inline mr-1" />
            Client *
          </label>
          <select
            id="client_id"
            name="client_id"
            required
            value={formData.client_id}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              fieldErrors.client_id 
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
            }`}
          >
            <option value="">Sélectionner un client</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name} - {client.company_name}
              </option>
            ))}
          </select>
          {fieldErrors.client_id && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-600 flex items-center"
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              {fieldErrors.client_id}
            </motion.p>
          )}
        </div>

        {/* Date */}
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="h-4 w-4 inline mr-1" />
            Date *
          </label>
          <input
            id="date"
            name="date"
            type="date"
            required
            value={formData.date}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              fieldErrors.date 
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
            }`}
          />
          {fieldErrors.date && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-600 flex items-center"
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              {fieldErrors.date}
            </motion.p>
          )}
        </div>

        {/* Time */}
        <div>
          <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
            <Clock className="h-4 w-4 inline mr-1" />
            Heure *
          </label>
          <input
            id="time"
            name="time"
            type="time"
            required
            value={formData.time}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              fieldErrors.time 
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
            }`}
          />
          {fieldErrors.time && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-600 flex items-center"
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              {fieldErrors.time}
            </motion.p>
          )}
        </div>
      </div>

      {/* Prospect Information */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Informations du prospect</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Prospect Name */}
          <div>
            <label htmlFor="prospect_name" className="block text-sm font-medium text-gray-700 mb-2">
              <User className="h-4 w-4 inline mr-1" />
              Nom du prospect *
            </label>
            <input
              id="prospect_name"
              name="prospect_name"
              type="text"
              required
              value={formData.prospect_name}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                fieldErrors.prospect_name 
                  ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                  : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
              }`}
              placeholder="Nom et prénom du prospect"
            />
            {fieldErrors.prospect_name && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-600 flex items-center"
              >
                <AlertCircle className="h-4 w-4 mr-1" />
                {fieldErrors.prospect_name}
              </motion.p>
            )}
          </div>

          {/* Job Title */}
          <div>
            <label htmlFor="job_title" className="block text-sm font-medium text-gray-700 mb-2">
              <Briefcase className="h-4 w-4 inline mr-1" />
              Poste
            </label>
            <input
              id="job_title"
              name="job_title"
              type="text"
              value={formData.job_title}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full rounded-xl border border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Ex: Directeur Marketing, CEO..."
            />
          </div>

          {/* Prospect LinkedIn */}
          <div>
            <label htmlFor="prospect_linkedin" className="block text-sm font-medium text-gray-700 mb-2">
              <User className="h-4 w-4 inline mr-1" />
              LinkedIn du prospect
            </label>
            <input
              id="prospect_linkedin"
              name="prospect_linkedin"
              type="url"
              value={formData.prospect_linkedin}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                fieldErrors.prospect_linkedin 
                  ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                  : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
              }`}
              placeholder="https://linkedin.com/in/..."
            />
            {fieldErrors.prospect_linkedin && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-600 flex items-center"
              >
                <AlertCircle className="h-4 w-4 mr-1" />
                {fieldErrors.prospect_linkedin}
              </motion.p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="h-4 w-4 inline mr-1" />
              Téléphone
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                fieldErrors.phone
                  ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
              }`}
              placeholder="+33 1 23 45 67 89"
            />
            {fieldErrors.phone && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-600 flex items-center"
              >
                <AlertCircle className="h-4 w-4 mr-1" />
                {fieldErrors.phone}
              </motion.p>
            )}
          </div>

          {/* Email Sales Hook */}
          <div>
            <label htmlFor="email_sales_hook" className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="h-4 w-4 inline mr-1" />
              Email Sales Hook
            </label>
            <input
              id="email_sales_hook"
              name="email_sales_hook"
              type="email"
              value={formData.email_sales_hook}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                fieldErrors.email_sales_hook
                  ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
              }`}
              placeholder="email@example.com"
            />
            {fieldErrors.email_sales_hook && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-600 flex items-center"
              >
                <AlertCircle className="h-4 w-4 mr-1" />
                {fieldErrors.email_sales_hook}
              </motion.p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Email utilisé pour lier ce rendez-vous aux sessions de prospection
            </p>
          </div>
        </div>
      </div>

      {/* Company Information */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Informations de l'entreprise</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Company Name */}
          <div>
            <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="h-4 w-4 inline mr-1" />
              Nom de l'entreprise *
            </label>
            <input
              id="company_name"
              name="company_name"
              type="text"
              required
              value={formData.company_name}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                fieldErrors.company_name 
                  ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                  : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
              }`}
              placeholder="Nom de l'entreprise du prospect"
            />
            {fieldErrors.company_name && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-600 flex items-center"
              >
                <AlertCircle className="h-4 w-4 mr-1" />
                {fieldErrors.company_name}
              </motion.p>
            )}
          </div>

          {/* Company LinkedIn */}
          <div>
            <label htmlFor="company_linkedin" className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="h-4 w-4 inline mr-1" />
              LinkedIn de l'entreprise
            </label>
            <input
              id="company_linkedin"
              name="company_linkedin"
              type="url"
              value={formData.company_linkedin}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                fieldErrors.company_linkedin 
                  ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                  : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500'
              }`}
              placeholder="https://linkedin.com/company/..."
            />
            {fieldErrors.company_linkedin && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-600 flex items-center"
              >
                <AlertCircle className="h-4 w-4 mr-1" />
                {fieldErrors.company_linkedin}
              </motion.p>
            )}
          </div>
        </div>
      </div>

      {/* Additional Details */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Détails supplémentaires</h3>
        <div className="space-y-6">
          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="h-4 w-4 inline mr-1" />
              Lieu du rendez-vous
            </label>
            <input
              id="location"
              name="location"
              type="text"
              value={formData.location}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full rounded-xl border border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Ex: Bureau client, Visioconférence, Restaurant..."
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="h-4 w-4 inline mr-1" />
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full rounded-xl border border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
              placeholder="Objectifs du rendez-vous, sujets à aborder..."
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="h-4 w-4 inline mr-1" />
              Notes internes
            </label>
            <textarea
              id="notes"
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full rounded-xl border border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
              placeholder="Notes privées, préparation, contexte..."
            />
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              <CheckCircle className="h-4 w-4 inline mr-1" />
              Statut
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full rounded-xl border border-gray-300 bg-white hover:border-gray-400 focus:border-blue-500 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="planifié">Planifié</option>
              <option value="confirmé">Confirmé</option>
              <option value="effectué">Effectué</option>
              <option value="annulé">Annulé</option>
              <option value="reporté">Reporté</option>
            </select>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={!isFormValid || isSubmitting}
          icon={isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
          className="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        >
          {isSubmitting ? 'Création en cours...' : 'Créer le rendez-vous'}
        </Button>
      </div>
    </form>
  );
};

export default AddAppointmentForm;